import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, activityFromPrice } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { BrandHeader } from "@/components/BrandHeader";
import { NavMenu } from "@/components/NavMenu";
import { ActivityCard } from "@/components/ActivityCard";
import { SportyPeopleBadge } from "@/components/SportyPeople";
import { ADULT_CATEGORIES, FAMILY_CATEGORIES, matchesAgeFilter } from "@/lib/ageCategories";
import { CalendarDays, X, SlidersHorizontal, Users, User, Tag, Clock, Layers, Sparkles } from "lucide-react";

const PRICES = [
  { id: "0-20", label: "Hasta 20 €", test: (p) => p <= 20 },
  { id: "20-50", label: "20–50 €", test: (p) => p > 20 && p <= 50 },
  { id: "50-100", label: "50–100 €", test: (p) => p > 50 && p <= 100 },
  { id: "100", label: "Más de 100 €", test: (p) => p > 100 },
];
const DURATIONS = [{ id: "dia", label: "De día sin pernocta" }, { id: "finde", label: "Fin de semana" }, { id: "puente", label: "Puentes o más de 3 días" }];
const MEDIUMS = [{ id: "nieve", label: "Nieve" }, { id: "tierra", label: "Tierra" }, { id: "mar", label: "Mar" }, { id: "rio", label: "Río" }, { id: "lago", label: "Lago" }];
const minPrice = (a) => activityFromPrice(a) ?? 0;
const durationOf = (a) => {
  if (!a.start_date) return null;
  const s = new Date(a.start_date + "T00:00:00");
  const e = new Date((a.end_date || a.start_date) + "T00:00:00");
  const days = Math.round((e - s) / 86400000) + 1;
  return days <= 1 ? "dia" : days <= 2 ? "finde" : "puente";
};

const chip = (active) => `rounded-full px-3.5 py-1.5 text-sm font-bold border-2 transition-colors ${active ? "border-[#003284] bg-[#003284] text-white" : "border-slate-200 text-slate-600 hover:border-[#003284]"}`;

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Icon size={13} /> {title}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function HomePage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { user, branding } = useApp();

  useEffect(() => {
    api.get("/activities").then((r) => setActivities(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const edad = sp.get("edad"), medium = sp.get("medium"), precio = sp.get("precio"), duracion = sp.get("duracion");
  const desde = sp.get("desde") || "", hasta = sp.get("hasta") || "";

  const setFilter = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };
  const clearAll = () => setSp({});
  const hasFilters = edad || medium || precio || duracion || desde || hasta;

  const filtered = useMemo(() => activities.filter((a) => {
    if (!matchesAgeFilter(a.age_categories, edad)) return false;
    if (medium && a.medium !== medium) return false;
    if (duracion && durationOf(a) !== duracion) return false;
    if (precio) { const b = PRICES.find((p) => p.id === precio); if (b && !b.test(minPrice(a))) return false; }
    if (desde && (a.start_date || "") < desde) return false;
    if (hasta && (a.start_date || "") > hasta) return false;
    return true;
  }).sort((x, y) => (x.start_date || "").localeCompare(y.start_date || "")), [activities, edad, medium, precio, duracion, desde, hasta]);

  const recommended = useMemo(() => {
    const kc = user?.kid_categories || [];
    if (!kc.length) return [];
    return activities
      .filter((a) => a.status !== "cancelada" && (a.age_categories || []).some((c) => kc.includes(c)))
      .sort((x, y) => (x.start_date || "").localeCompare(y.start_date || ""))
      .slice(0, 3);
  }, [activities, user]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* HERO familia en acción, luz cálida + velo lateral para legibilidad */}
      <div className="relative border-b border-slate-100 overflow-hidden">
        <div className="absolute inset-0 bg-cover" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1566583389556-fc7376f4ecd8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)", backgroundPosition: "center 78%" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-white/25" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-[#F8FAFC]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-10">
          <div className="flex justify-end"><NavMenu /></div>
          <div className="mt-4 sm:mt-6 max-w-3xl">
            <p className="font-bold text-[#FF7F27] uppercase tracking-widest text-xs sm:text-sm">{user ? `Hola, ${user.name?.split(" ")[0]}` : "Bienvenido/a"}</p>
            <h1 className="text-3xl sm:text-5xl font-black brand-navy leading-tight mt-1">Actividades de día y Viajes en la Naturaleza</h1>
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <span className="text-base sm:text-lg font-bold text-slate-600">Grupos de Empresa de</span>
              {branding?.logo_url && <img data-testid="hero-repsol-logo" src={branding.logo_url} alt="Repsol" className="h-9 sm:h-11 w-auto" />}
            </div>
            <div className="mt-2"><SportyPeopleBadge compact /></div>
            <p className="text-slate-700 font-medium mt-4 max-w-xl">Filtra por participantes, entorno, duración, fecha o precio y encuentra el plan perfecto.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button data-testid="home-ver-calendario" onClick={() => navigate("/calendario")} className="inline-flex items-center gap-2 bg-[#003284] text-white font-bold rounded-full px-6 py-3 hover:bg-[#00246a] transition-colors"><CalendarDays size={18} /> Ver en calendario</button>
              <button data-testid="home-ver-actividades" onClick={() => document.getElementById("actividades")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border-2 border-[#003284]/30 text-[#003284] font-bold rounded-full px-6 py-3 hover:border-[#003284] hover:bg-white transition-colors"><Layers size={18} /> Próximas actividades</button>
            </div>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="bg-white rounded-3xl p-4 sm:p-5 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <SlidersHorizontal size={18} className="brand-navy" />
            <h2 className="font-black brand-navy text-lg">Encuentra tu actividad</h2>
            {hasFilters && <button data-testid="clear-filters" onClick={clearAll} className="ml-auto flex items-center gap-1 text-sm font-bold text-[#E31837]"><X size={15} /> Limpiar filtros</button>}
          </div>
          <div className="mb-4">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Users size={13} /> ¿Para quién?</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-2xl border-2 border-[#003284]/25 bg-[#003284]/[0.05] p-3">
                <div className="flex items-center gap-2 mb-2.5">
                  <Users size={16} className="text-[#003284]" />
                  <span className="text-xs font-black uppercase tracking-wide text-[#003284]">Adultos</span>
                  <span className="text-[11px] font-semibold text-slate-500">— solo mayores</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ADULT_CATEGORIES.map((c) => <button key={c.id} data-testid={`filter-age-${c.id}`} onClick={() => setFilter("edad", c.id)} className={chip(edad === c.id)}>{c.label} <span className="opacity-60">{c.range}</span></button>)}
                </div>
              </div>
              <div className="rounded-2xl border-2 border-[#FF7F27]/35 bg-[#FF7F27]/[0.06] p-3">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="flex items-end text-[#FF7F27] -space-x-0.5"><User size={18} strokeWidth={2.25} /><User size={12} strokeWidth={2.25} /></span>
                  <span className="text-xs font-black uppercase tracking-wide text-[#FF7F27]">Familiares</span>
                  <span className="text-[11px] font-semibold text-slate-500">— elige edad o todas</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="filter-age-familiares" onClick={() => setFilter("edad", "familiares")} className={chip(edad === "familiares")}>Todas las familiares</button>
                  {FAMILY_CATEGORIES.map((c) => <button key={c.id} data-testid={`filter-age-${c.id}`} onClick={() => setFilter("edad", c.id)} className={chip(edad === c.id)}>{c.label} <span className="opacity-60">{c.range}</span></button>)}
                </div>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            <Section icon={Layers} title="Tipo de actividad (entorno)">
              <button data-testid="filter-medium-all" onClick={() => setFilter("medium", "")} className={chip(!medium)}>Todas</button>
              {MEDIUMS.map((mm) => <button key={mm.id} data-testid={`filter-medium-${mm.id}`} onClick={() => setFilter("medium", mm.id)} className={chip(medium === mm.id)}>{mm.label}</button>)}
            </Section>
            <Section icon={Clock} title="Duración">
              <button data-testid="filter-duration-all" onClick={() => setFilter("duracion", "")} className={chip(!duracion)}>Todas</button>
              {DURATIONS.map((d) => <button key={d.id} data-testid={`filter-duration-${d.id}`} onClick={() => setFilter("duracion", d.id)} className={chip(duracion === d.id)}>{d.label}</button>)}
            </Section>
            <Section icon={Tag} title="Precio">
              <button data-testid="filter-price-all" onClick={() => setFilter("precio", "")} className={chip(!precio)}>Todos</button>
              {PRICES.map((p) => <button key={p.id} data-testid={`filter-price-${p.id}`} onClick={() => setFilter("precio", p.id)} className={chip(precio === p.id)}>{p.label}</button>)}
            </Section>
            <Section icon={CalendarDays} title="Fechas">
              <button data-testid="filter-date-all" onClick={() => { const n = new URLSearchParams(sp); n.delete("desde"); n.delete("hasta"); setSp(n); }} className={chip(!desde && !hasta)}>Todas las fechas</button>
              <div className="flex items-center gap-2">
                <input data-testid="filter-date-from" type="date" value={desde} onChange={(e) => setFilter("desde", e.target.value)} className="rounded-xl border-2 border-slate-200 px-3 py-1.5 text-sm font-semibold" />
                <span className="text-slate-400 text-sm">a</span>
                <input data-testid="filter-date-to" type="date" value={hasta} onChange={(e) => setFilter("hasta", e.target.value)} className="rounded-xl border-2 border-slate-200 px-3 py-1.5 text-sm font-semibold" />
              </div>
            </Section>
          </div>
        </div>
      </div>

      {!hasFilters && recommended.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8" data-testid="recommended-section">
          <div className="rounded-3xl border-2 border-[#FF7F27]/30 bg-[#FF7F27]/[0.05] p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={18} className="text-[#FF7F27]" />
              <h2 className="font-black brand-navy text-lg">Recomendado para tu familia</h2>
              <span className="text-sm font-semibold text-slate-500 hidden sm:inline">según las edades de tus hijos/as</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recommended.map((a) => <ActivityCard key={a.id} a={a} />)}
            </div>
          </div>
        </div>
      )}

      {/* RESULTS */}
      <div id="actividades" className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h2 className="text-xl font-black brand-navy mb-5">{hasFilters ? `${filtered.length} actividad${filtered.length === 1 ? "" : "es"} encontrada${filtered.length === 1 ? "" : "s"}` : "Próximas actividades"}</h2>
        {loading ? <p className="text-center text-slate-400 py-16">Cargando…</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">No hay actividades con estos filtros. <button onClick={clearAll} className="font-bold text-[#003284] underline">Ver todas</button></p>}
            {filtered.map((a) => <ActivityCard key={a.id} a={a} />)}
          </div>
        )}
      </div>

      <footer className="border-t border-slate-200 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-slate-400">Actividades para grupos de empresa · Repsol</p>
          <SportyPeopleBadge />
        </div>
      </footer>
    </div>
  );
}
